import { computed } from 'vue'
import { useBreakpoints } from '@vueuse/core'
import { useRoute } from 'vue-router'
import { useTranslation } from 'i18next-vue'
import { useAuthStore } from '@/stores/auth.store'
import { useNavContextStore } from '@/stores/nav-context.store'
import type { AppMenuItem } from '@/types/menu'
import { getCmsPageRoute } from '@/utils/cms-pages'
import { isRouteVisible } from '@/utils/router-helper'
import { useTestimonials } from '@/composables/useTestimonials'

function buildMobileGroup(group: AppMenuItem): AppMenuItem[] {
    const groupItems = (group.items ?? []) as AppMenuItem[]
    if (!groupItems.length) return []

    const [header, ...children] = groupItems
    const { isColumnHeader: _removed, ...headerRest } = header
    return [
        {
            ...headerRest,
            root: true,
            items: children.length ? ([[{ items: children }]] as AppMenuItem[][]) : undefined,
        },
    ]
}

function flattenMenuItemsForMobile(items: AppMenuItem[]): AppMenuItem[] {
    return items.flatMap((item) => {
        if (!item.items) return [item]

        // Normalize MegaMenu's two supported item layouts (column-matrix and flat
        // array) into the flat list consumed by buildMobileGroup for the mobile model.
        if (Array.isArray(item.items[0])) {
            const columns = item.items as AppMenuItem[][]
            return columns.flatMap((column) => column.flatMap((group) => buildMobileGroup(group)))
        } else {
            const groups = item.items as AppMenuItem[]
            return groups.flatMap((group) => (group.items ? buildMobileGroup(group) : [group]))
        }
    })
}

export function useMenuItems() {
    const route = useRoute()
    const { t } = useTranslation()
    const auth = useAuthStore()
    const navContext = useNavContextStore()
    const { testimonialsExist } = useTestimonials()

    const breakpoints = useBreakpoints({ md: 768 })
    const isMobile = breakpoints.smaller('md')

    const isRegistrationRoute = computed(() => Boolean(route.meta.disableSubNavigation))
    const isOrganizerRoute = computed(() => navContext.context === 'organizer')

    const menuItems = computed<AppMenuItem[]>(() => {
        if (isRegistrationRoute.value) return []

        const items: AppMenuItem[] = isOrganizerRoute.value
            ? [
                  {
                      label: t('ui.navigation.designParticipation'),
                      to: { name: 'organizer-landing-page' },
                      testid: 'nav-design-participation-link',
                      root: true,
                  },
                  {
                      label: t('ui.navigation.ressourcen'),
                      testid: 'nav-ressourcen-link',
                      root: true,
                      items: [
                          [
                              {
                                  items: [
                                      {
                                          label: t('ui.navigation.ressourcen'),
                                          isColumnHeader: true,
                                      },
                                      {
                                          label: t('ui.navigation.develop-formats.label'),
                                          sublabel: t('ui.navigation.develop-formats.sublabel', ''),
                                          to: getCmsPageRoute('/ressourcen/formate-entwickeln'),
                                          testid: 'navbar-navigate-ressourcen-formate-entwickeln',
                                      },
                                      {
                                          label: t('ui.navigation.setup-listing.label'),
                                          sublabel: t('ui.navigation.setup-listing.sublabel', ''),
                                          to: getCmsPageRoute(
                                              '/ressourcen/inserat-und-anmeldung-einrichten'
                                          ),
                                          testid: 'navbar-navigate-ressourcen-inserat-anmeldung',
                                      },
                                      {
                                          label: t('ui.navigation.messages.label'),
                                          sublabel: t('ui.navigation.messages.sublabel', ''),
                                          to: getCmsPageRoute('/ressourcen/nachrichten'),
                                          testid: 'navbar-navigate-ressourcen-nachrichten',
                                      },
                                      // TODO: Uncomment and link to page/route once available.
                                      // {
                                      //     label: t('ui.navigation.get-inspired.label'),
                                      //     sublabel: t('ui.navigation.get-inspired.sublabel', ''),
                                      //     url: '/child',
                                      // },
                                      // TODO: Uncomment once page has content.
                                      // {
                                      //     label: t('ui.navigation.rights.label'),
                                      //     sublabel: t('ui.navigation.rights.sublabel', ''),
                                      //     command: () => router.push(getCmsPageRoute('/rechte')),
                                      // },
                                      // NOTE: Not part of the MVP
                                      // {
                                      //     label: t('ui.navigation.participation-minors.label'),
                                      //     sublabel: t(
                                      //         'ui.navigation.participation-minors.sublabel',
                                      //         ''
                                      //     ),
                                      //     to: getCmsPageRoute('/ressourcen/teilnahme-unter-18'),
                                      // },
                                      {
                                          label: t('ui.navigation.ressourcen-faq.label'),
                                          to: getCmsPageRoute('/ressourcen/faq'),
                                          testid: 'navbar-navigate-ressourcen-faq',
                                      },
                                  ],
                              },
                          ],
                      ],
                  },
              ]
            : [
                  {
                      label: t('ui.navigation.discover'),
                      to: { name: 'explore' },
                      testid: 'nav-discover-link',
                      root: true,
                  },
                  {
                      label: t('ui.navigation.impressions'),
                      to: { name: 'testimonials' },
                      testid: 'nav-impressions-link',
                      root: true,
                      condition: () => testimonialsExist.value,
                  },
                  {
                      label: t('ui.navigation.faq'),
                      testid: 'nav-faq-link',
                      root: true,
                      items: [
                          [
                              {
                                  items: [
                                      {
                                          label: t('ui.navigation.faq'),
                                          isColumnHeader: true,
                                      },
                                      {
                                          label: t('ui.navigation.about-us-intro.label'),
                                          sublabel: t('ui.navigation.about-us-intro.sublabel', ''),
                                          to: getCmsPageRoute('/ueber-uns'),
                                          testid: 'navbar-navigate-mehr-ueber-uns',
                                      },
                                      {
                                          label: t('ui.navigation.participation-explained.label'),
                                          sublabel: t(
                                              'ui.navigation.participation-explained.sublabel',
                                              ''
                                          ),
                                          to: getCmsPageRoute(
                                              '/ressourcen/beteiligung-kurz-erklaert'
                                          ),
                                          testid: 'navbar-navigate-faq-beteiligung-erklaert',
                                      },
                                      {
                                          label: t('ui.navigation.glossary.label'),
                                          sublabel: t('ui.navigation.glossary.sublabel', ''),
                                          to: getCmsPageRoute('/ressourcen/begriffe-kurz-erklaert'),
                                          testid: 'navbar-navigate-faq-begriffe-erklaert',
                                      },
                                      // TODO: Not part of the MVP
                                      // {
                                      //     label: t('ui.navigation.join-formats.label'),
                                      //     sublabel: t('ui.navigation.join-formats.sublabel', ''),
                                      //     to: getCmsPageRoute('/ressourcen/an-formaten-teilnehmen'),
                                      //     testid: 'navbar-navigate-faq-formate-teilnehmen',
                                      // },
                                      {
                                          label: t('ui.navigation.faq-participants.label'),
                                          to: getCmsPageRoute('/ressourcen/faq-fuer-teilnehmende'),
                                          testid: 'navbar-navigate-faq-teilnehmende',
                                      },
                                      {
                                          label: t('ui.navigation.faq-guardians.label'),
                                          sublabel: t('ui.navigation.faq-guardians.sublabel', ''),
                                          to: getCmsPageRoute(
                                              '/ressourcen/faq-fuer-erziehungsberechtigte'
                                          ),
                                          testid: 'navbar-navigate-faq-erziehungsberechtigte',
                                      },
                                  ],
                              },
                          ],
                      ],
                  },
                  {
                      label: t('ui.navigation.more'),
                      testid: 'nav-more-link',
                      to: { name: 'contact' },
                      root: true,
                  },
              ]

        if (auth.user) {
            items.push({
                label: t('ui.navigation.profile'),
                to: { name: 'profile' },
                testid: 'nav-profile-link',
                root: true,
            })
        } else {
            items.push({
                label: t('ui.navigation.landing-organizer'),
                class: 'sm:hidden',
                to: { name: 'organizer-landing-page' },
                testid: 'nav-mobile-organizer-link',
                root: true,
            })
            items.push({
                label: t('ui.navigation.landing-participant'),
                class: 'sm:hidden',
                to: { name: 'register-participant' },
                testid: 'nav-mobile-participant-register-link',
                root: true,
            })
        }

        return items
            .filter((item) => (item.condition ? item.condition() : true))
            .filter((item) => !item.to || isRouteVisible(item.to))
    })

    const responsiveMenuItems = computed<AppMenuItem[]>(() =>
        isMobile.value ? flattenMenuItemsForMobile(menuItems.value) : menuItems.value
    )

    return { menuItems, responsiveMenuItems, isRegistrationRoute, isOrganizerRoute }
}
